import { call } from "./callFunction";
import type { DraftLine, ExpenseDraft } from "../components/ExpenseEditor";

// Scan a supplier invoice or receipt (parseVendorInvoice) and turn the
// result into an expense draft for review. Shared by the receiving dock
// (matches parts on order across the company) and a job's "Add receipt".

export interface ScanMatch {
  orderedItemId: string;
  lineIndex: number;
  lineDescription: string;
  confidence: "high" | "medium" | "low";
  item: { id: string; jobId: string; description: string; vendor: string; quantity: number; itemId: string | null };
}

export interface ScanResult {
  vendorName: string | null;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  lines: Array<{ description: string; quantity: number; unitCostCents: number | null; extendedCents: number | null; sku: string }>;
  charges: Array<{ type: "shipping" | "fuel" | "tax" | "adjustment" | "other"; label: string; amountCents: number }>;
  totalCents: number | null;
  totalMismatch: boolean;
  matches: ScanMatch[];
  confidence: string;
  openCount: number;
  documentPath: string | null;
}

export const SCAN_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/webp"];

export async function scanExpenseDoc(tenantId: string, file: File, jobId: string | null = null): Promise<ScanResult> {
  // iPhones hand over HEIC as "image/heic"; the camera input asks for JPEG where it can.
  const mediaType = file.type || "application/pdf";
  if (!SCAN_TYPES.includes(mediaType)) throw new Error("PDF or photo (JPG/PNG) only.");
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return call<ScanResult>("parseVendorInvoice", { tenantId, docBase64: btoa(binary), mediaType, jobId });
}

/**
 * Lines matched to an ordered part inherit that part's job (and pricebook
 * item), so each part's cost lands on the job it was bought for.
 */
export function draftFromScan(scan: ScanResult, opts: { jobId?: string | null; useMatches?: Set<string> } = {}): ExpenseDraft {
  const byLine = new Map<number, ScanMatch>();
  for (const m of scan.matches) {
    if (!opts.useMatches || opts.useMatches.has(m.orderedItemId)) byLine.set(m.lineIndex, m);
  }
  const lines: DraftLine[] = scan.lines.map((l, i) => {
    const m = byLine.get(i);
    const unit = l.unitCostCents ?? 0;
    return {
      description: l.description,
      quantity: l.quantity,
      unitCostCents: unit,
      extendedCents: l.extendedCents ?? Math.round(unit * l.quantity),
      sku: l.sku,
      jobId: opts.jobId ?? m?.item.jobId ?? null,
      orderedItemId: m?.orderedItemId ?? null,
      itemId: m?.item.itemId ?? null,
    };
  });
  const jobIds = [...new Set(lines.map((l) => l.jobId).filter(Boolean))] as string[];
  return {
    vendorName: scan.vendorName ?? "",
    invoiceNumber: scan.invoiceNumber ?? "",
    invoiceDate: scan.invoiceDate,
    jobId: opts.jobId ?? (jobIds.length === 1 ? jobIds[0]! : null),
    lines,
    surcharges: scan.charges.map((c) => ({ type: c.type, label: c.label, amountCents: c.amountCents })),
    note: "",
  };
}

export async function saveExpense(
  tenantId: string,
  draft: ExpenseDraft,
  opts: { source: "receiving" | "receipt" | "manual"; documentPath?: string | null },
): Promise<{ expenseId: string; number: number; status: string }> {
  return call("createExpense", {
    tenantId,
    source: opts.source,
    documentPath: opts.documentPath ?? null,
    vendorName: draft.vendorName.trim(),
    invoiceNumber: draft.invoiceNumber.trim(),
    invoiceDate: draft.invoiceDate,
    jobId: draft.jobId,
    lines: draft.lines.map(({ id, ...l }) => (id ? { id, ...l } : l)),
    surcharges: draft.surcharges.map(({ id, ...s }) => (id ? { id, ...s } : s)),
    note: draft.note,
  });
}
